<template>
    <b-card v-if="isFetchingRequiredData">
        <loader data-type="FormInput12WithTitle" class="custom-loading" />
    </b-card>
   
    <div v-else>
        <b-card class="formations generate-transfer transfer-company">
            <b-card
                title="Generate Transfer Documents"
                class="mt-2 bordered-card generate-transfer-section"
            >
                <validation-observer ref="generateTransferCompanyDocuments">
                    <validation-provider
                        v-slot="{ errors }"
                        name="secretary firm"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Secretary firm" :state="errors.length > 0 ? false : null">
                            <v-select
                                ref="secretaryFirm"
                                v-model="signingForm.secretaryFirm"
                                class="custom-select-icon"
                                :clearable="false"
                                placeholder="Select secretary firm"
                                label="name"
                                value="id"
                                :options="secretaryFirmOptions"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="recommending officer"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Recommending officer" :state="errors.length > 0 ? false : null">
                            <v-select
                                ref="recommendingOfficer"
                                v-model="signingForm.recommendingOfficer"
                                class="custom-select-icon"
                                :clearable="false"
                                placeholder="Select recommending officer"
                                value="id"
                                label="text"
                                :options="recommendingOfficerOptions"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="approving officer"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Approving officer" :state="errors.length > 0 ? false : null">
                            <v-select
                                ref="approvingOfficer"
                                v-model="signingForm.approvingOfficer"
                                class="custom-select-icon"
                                :clearable="false"
                                placeholder="Select approving officer"
                                value="id"
                                label="text"
                                :options="approvingOfficerOptions"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="auditor name"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Auditor name" :state="errors.length > 0 ? false : null">
                            <v-select
                                    ref="auditorName"
                                    v-model="signingForm.auditor"
                                    class="custom-select-icon"
                                    :clearable="true"
                                    placeholder="Select auditor name"
                                    label="name"
                                    value="uen"
                                    :options="auditorOptions"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="secretary name"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Secretary name" :state="errors.length > 0 ? false : null">
                            <v-select
                                ref="secretaryName"
                                v-model="signingForm.secretary"
                                class="custom-select-icon"
                                :clearable="false"
                                placeholder="Select secretary name"
                                label="name"
                                value="id"
                                :options="secretaryOptions"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="secretary effective from date"
                        rules="required"
                    >
                        <b-form-group label="Consent to act as secretary is effective from" :state="errors.length > 0 ? false : null">
                            <flat-pickr
                                ref="effectiveFrom"
                                v-model="signingForm.effectiveFrom"
                                placeholder="Enter secretary effective from"
                                class="form-control"
                                :config="datePickerConfig"
                                data-test="effective-from-date"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="operating hour"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Operating hours" :state="errors.length > 0 ? false : null">
                            <v-select
                                ref="operatingHour"
                                v-model="signingForm.operatingHour"
                                class="custom-select-icon"
                                :clearable="false"
                                placeholder="Select operating hour"
                                value="id"
                                label="description"
                                :options="operatingHoursOptions"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="registration number"
                        rules="required"
                    >
                        <b-form-group label="Registration number">
                            <b-form-input
                                ref="registrationNumber"
                                v-model="signingForm.registrationNumber"
                                placeholder="Enter registration number"
                                :state="errors.length > 0 ? false : null"
                                data-test="registration-number"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="secretary renewal fee"
                        rules="required"
                    >
                        <b-form-group label="Secretary renewal fee">
                            <b-form-input
                                ref="secretaryRenewalFee"
                                v-model="signingForm.secretaryRenewalFee"
                                v-mask="amountMask"
                                placeholder="Enter secretary renewal fee"
                                :state="errors.length > 0 ? false : null"
                                data-test="secretary-renewal-fee"
                                type="text"
                                no-wheel
                                @keydown.up.prevent
                                @keydown.down.prevent
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="incorporation date"
                        rules="required"
                    >
                        <b-form-group label="Incorporation date" :state="errors.length > 0 ? false : null">
                            <flat-pickr
                                ref="incorporationDate"
                                v-model="signingForm.incorporationDate"
                                placeholder="Enter incorporation date"
                                class="form-control"
                                :config="datePickerConfig"
                                data-test="incorporation-date"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="share drive"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group>
                            <slot name="label">
                                <label id="share-drive" for="share-drive">
                                    Share drive
                                    <vector
                                        data-type="icon"
                                        data-width="14px"
                                        data-height="14px"
                                        data-class="mr-.5"
                                        data-name="information"
                                        style="position:relative; bottom:1px;"
                                    ></vector>
                                </label>
                            </slot>

                            <b-popover
                                target="share-drive"
                                triggers="hover"
                                placement="right"
                                variant="black"
                            >
                                Please provide the shared drive of the company being taken over.
                            </b-popover>

                            <v-select
                                ref="shareDrive"
                                v-model="signingForm.shareDrive"
                                class="custom-select-icon share-drive"
                                :clearable="false"
                                placeholder="Select share drive"
                                value="id"
                                label="name"
                                :disabled="! shareDriveEditable"
                                :options="shareDriveOptions"
                                @input="handleOnShareDriveChange"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="company"
                        class="end-align-indicator-long-select company-options"
                    >
                        <b-form-group :state="errors.length > 0 ? false : null" >
                            <slot name="label">
                                <label id="company" for="company">
                                    Company 
                                    <vector
                                        data-type="icon"
                                        data-width="14px"
                                        data-height="14px"
                                        data-class="mr-.5"
                                        data-name="information"
                                        style="position:relative; bottom:1px;"
                                    ></vector>
                                </label>
                            </slot>

                            <b-popover
                                target="company"
                                triggers="hover"
                                placement="right"
                                variant="black"
                            >
                                Please provide the company from the shared drive that is being taken over.
                            </b-popover>

                            <v-select
                                id="company"
                                ref="company"
                                v-model="signingForm.shareDriveCompany"
                                class="custom-select-icon"
                                :clearable="false"
                                placeholder="Select company"
                                label="name"
                                :disabled="! shareDriveEditable"
                                :options="shareDriveCompanyOptions"
                            >
                                <div slot="no-options" class="d-flex justify-content-center">
                                    <normal-loader v-if="isFetchingCompanies" />
                                    <span v-else>{{ shareDriveEmptyMessage }}</span>
                                </div>
                            </v-select>
                        </b-form-group>
                    </validation-provider>
                </validation-observer>

                <b-button
                    block
                    class="mt-2"
                    variant="primary"
                    data-test="generate-signing-btn"
                    :disabled="loading || isFetchingRequiredData"
                    @click="generateTransferCompanyDocuments"
                >
                    <span> {{ companyTransferButtonLabel }} </span>

                    <img
                        v-if="!loading"
                        :src="'/icons/papers_white.svg'"
                        class="float-right"
                        alt="add-logo"
                    />
                    <normal-loader v-if="loading" class="float-right" />
                </b-button>
            </b-card>

            <div class="d-flex justify-content-between mt-3">
                <b-button
                    variant="outline-primary"
                    class="text-primary"
                    @click="$emit('previous')"
                >
                    <vector data-type="icons" data-width="16px" data-height="13px" data-name="left-arrow" />
                    Previous
                </b-button>
            </div>
        </b-card>

        <!-- confirm to re-generate --> 
        <confirmation-modal  ref="modal" size="md">
            <template slot="body">
                <div>
                    <feather-icon class="d-block mx-auto text-warning" icon="AlertCircleIcon" size="90"/> 
                    <h1 class="text-center text-secondary mt-2" style="line-height: 25px; font-size: 1.155rem;">
                        The {{ PROCESS.SECRETARY_APPOINTMENT }} of some documents have already been signed. Are you sure that you want to delete all of the signed documents and regenerate them?
                    </h1>
                    <p class="text-center mt-2"> You won't be able to revert this! </p>
                </div>
            </template>

            <template slot="footer">
                <b-button class="d-flex" :disabled="isSideBarActionLoading" size="md" variant="primary" @click="confirm">
                    Yes, Proceed!
                    <normal-loader v-if="isSideBarActionLoading" class="left-10%" />
                </b-button>

                <b-button size="md" variant="outline-danger" @click="cancel">
                    Cancel
                </b-button>
            </template>
        </confirmation-modal>
    </div>
</template>
<script>

import { ValidationProvider, ValidationObserver } from "vee-validate";
import { BFormGroup, BCard, BFormInput, BButton, BPopover } from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { required, email } from "@validations";
import ConfirmationModal from '@/views/components/utils/ConfirmationModal.vue';
import SigningForm from '@/models/SigningForm';
import GenerateDocument from '@/models/GenerateDocument';
import ShareDrive from '@/models/ShareDrive';
import { DATE_PICKER_CONFIG, COMPANY, PROCESS } from '@/constants/config';
import { errorToast, successToast } from '@/views/helpers/toast';
import { amountMask, unMask, formatDate, formatPrice } from "@/helpers";
import { NO_MATCHING_OPTIONS, SOMETHING_WENT_WRONG  } from '@/constants/errors';
import { GENERATING_TRANSFER_COMPANY_DOCUMENT, REGENERATING_TRANSFER_COMPANY_DOCUMENT } from '@/constants/success';

export default {
    components:{
        ValidationProvider,
        ValidationObserver,
        BFormGroup,
        BFormInput,
        BCard,
        BButton,
        BPopover,
        vSelect,
        flatPickr,

        ConfirmationModal
    },

    data(){
        return {
            required,
            email,

            status: "create",
            loading: false,

            auditorOptions: [],
            secretaryOptions: [],
            operatingHoursOptions: [],
            approvingOfficerOptions: [],
            recommendingOfficerOptions: [],
            
            shareDriveOptions: [],
            secretaryFirmOptions: [],
            shareDriveCompanyOptions: [],

            signingForm: {
                secretaryFirm: null,
                secretary: null,
                effectiveFrom: null,
                auditor: null,
                operatingHour: null,
                registrationNumber: null,
                incorporationDate: null,
                shareDrive: null,
                secretaryRenewalFee: null,
                isIncorporated: null,
                remark: null,
                shareDriveCompany: null,
            },

            isProcessHasSignSignatureDocuments: false,
            shareDriveEditable: false,
            proceed: false,
            PROCESS,
            isSideBarActionLoading: false,

            datePickerConfig: DATE_PICKER_CONFIG,

            maskKeys:['secretaryRenewalFee'],
            amountMask,
            NO_MATCHING_OPTIONS,
            isFetchingCompanies: false,
            isFetchingRequiredData: false,
        };
    },

    computed: {
        companyTransferButtonLabel() {
            if(
                this.signingForm.remark === COMPANY.GENERATED_TRANSFER_COMPANY_REMARK ||
                this.signingForm.remark === COMPANY.FINISHED_TRANSFER_COMPANY_REMARK
            )
                return "Regenerate Transfer Documents";
            return "Generate Transfer Documents";
        },

        shareDriveEmptyMessage() {
            if(! this.signingForm.shareDriveCompany) {
                return "Please select share drive first.";
            }

            return NO_MATCHING_OPTIONS;
        }
    },

    async created() {
        this.isFetchingRequiredData = true;

        // is process has sing signature documents
        this.isProcessHasSignSignatureDocuments = await GenerateDocument.isProcessHasSignSignatureDocs(PROCESS.SECRETARY_APPOINTMENT);
        
        const response = await SigningForm.getTransferCompanyFormData();

        // SETUP LOCAL DATA
        this.setupLocalData(response);

        // OPTION DATA ----------------------------------------------------------------------------
        this.recommendingOfficerOptions = response.recommending_officers.map(officer => ({
            text: officer.user?.user_info?.legal_name ?? officer.user.name,
            value: officer.id
        }));
        
        this.approvingOfficerOptions = response.approving_officers.map(officer => ({
            text: officer.user?.user_info?.legal_name ?? officer.user.name,
            value: officer.id
        }));
        
        this.secretaryOptions = response.secretaries.map(secretary => ({
            id: secretary.id,
            userId: secretary.user.id,
            name: secretary.user?.user_info?.legal_name ?? secretary.user.name,
        }));
       
        this.auditorOptions = response.auditors.map(auditor => ({
            name: auditor.name,
            id: auditor.id,
        }));
       
        this.secretaryFirmOptions = response.secretary_firms;
        this.operatingHoursOptions = response.operating_hours;
        this.shareDriveOptions = response.share_drives;
        // END OPTION ----------------------------------------------------------------------

        this.isFetchingRequiredData = false;
    },

    methods: {
        setupLocalData(data) {
            this.signingForm.isIncorporated = data.isIncorporated;

            this.signingForm.secretaryRenewalFee = data.renewal_fee ? formatPrice(data.renewal_fee) : null;

            this.signingForm.secretaryFirm = data.company_information?.secretary_firm ? {
                id: data.company_information.secretary_firm.id,
                name: data.company_information.secretary_firm.name
            } : null;

            this.signingForm.remark = data.company_information?.remark;

            this.signingForm.recommendingOfficer = data.company_recommending_officer ? {
                text: data.company_recommending_officer.user?.user_info?.legal_name ?? data.company_recommending_officer.user.name,
                value: data.company_recommending_officer.id
            } : null;

            this.signingForm.approvingOfficer = data.company_approving_officer ? {
                text: data.company_approving_officer.user?.user_info?.legal_name ?? data.company_approving_officer.user.name,
                value: data.company_approving_officer.id
            } : null;

            this.signingForm.shareDrive = data.company_share_drive;
          
            this.signingForm.registrationNumber = data.company_information ? data.company_information.uen : "";

            this.signingForm.operatingHour = data.company_operating_hour ? {
                id: data.company_operating_hour.operating_hour_id,
                description: data.company_operating_hour.operating_hour.description
            } : null;

            this.signingForm.auditor = data.company_auditor ? {
                id: data.company_auditor.auditor_id,
                name: data.company_auditor.auditor.name
            } : null;

            this.signingForm.secretary = data.company_secretary ? {
                id: data.company_secretary.user_id,
                userId: data.company_secretary.user_id,
                name: data.company_secretary.user?.user_info?.legal_name ?? data.company_secretary.user.name
            } : null;

            this.signingForm.effectiveFrom = data.company_secretary ? formatDate(data.company_secretary.appointment_date) : null;

            // incorporation date
            this.signingForm.incorporationDate = data.company_information ? formatDate(data.company_information.incorporation_date) : "";

            // this.signingForm.shareDriveCompany = data.company_information?.secretary_folder_path?.split('/')[1];
            if(data.shareDriveCompany) {
                this.shareDriveEditable = false;
                this.signingForm.shareDriveCompany = data.shareDriveCompany;
            } else {
                this.shareDriveEditable = true;
            }
        },

        async handleOnShareDriveChange(shareDrive) {
            this.isFetchingCompanies = true;

            this.shareDriveCompanyOptions = [];
            this.signingForm.shareDriveCompany = undefined;
            
            const shareDriveCompanies = await ShareDrive.getAllCompaniesOfCurrentShareDrive(shareDrive);
            this.shareDriveCompanyOptions = shareDriveCompanies;

            this.isFetchingCompanies = false;
        },

        async generateTransferCompanyDocuments()
        {
            this.$refs.generateTransferCompanyDocuments.validate().then(success => {
                if (success)
                {
                    if(this.isProcessHasSignSignatureDocuments && !this.proceed) {
                        this.$refs.modal.$refs.confirmationModal.show();
                        return;
                    }
                    this.$refs.modal.$refs.confirmationModal.hide();
                    this.isSideBarActionLoading = false;

                    this.loading = true;
                    SigningForm
                        .generateTransferCompanyDocuments(unMask(this.signingForm, this.maskKeys))
                        .then(()=> {
                            this.loading = false;
                            successToast( (this.signingForm.isIncorporated) ? REGENERATING_TRANSFER_COMPANY_DOCUMENT: GENERATING_TRANSFER_COMPANY_DOCUMENT);
                            this.$router.push({ path: '/formations' });
                        })
                        .catch(e => {
                            this.loading = false;
                            
                            if(e.response.data?.error?.incorporationDate) {
                                errorToast(e.response.data.error.incorporationDate[0]);
                            } else if(e.response.data.message) {
                                errorToast(e.response.data.message);
                            }
                            else{
                                errorToast(SOMETHING_WENT_WRONG);
                            }

                            // eslint-disable-next-line no-console
                            console.log('Error while throwing signing form', e);
                        });
                }
            });
        },

        confirm() {
            this.proceed = true;
            this.isSideBarActionLoading = true;
            this.generateTransferCompanyDocuments();
        },

        cancel() {
            this.proceed = false;
            this.$refs.modal.$refs.confirmationModal.hide();
        }
    },
};
</script>

<style>
.generate-transfer.transfer-company .share-drive .vs__dropdown-option--highlight {
    background: white;
    color: black;
}

.generate-transfer.transfer-company .share-drive .vs__dropdown-option--selected,
.generate-transfer.transfer-company .share-drive .vs__dropdown-option.vs__dropdown-option--selected {
    background: rgba(66, 103, 178, 0.12) !important;
    color: var(--primary) !important;
}

/* preloader */
.transfer-company .company-options .preloader {
    width: 15px;
    height: 15px;
}

.transfer-company .company-options .preloader div:before {
    background: var(--primary-color);
}

.transfer-company .company-options .action-button .preloader div:before {
    background: white;
}
</style>
